import React, { useState, useEffect, useCallback } from 'react';
import { X, Trash2, Sparkles, Save } from 'lucide-react';
import {
  analyzeMemorySource,
  listMemoryFiles,
  saveMemoryFile,
  deleteMemoryFile,
} from './courseService';

const SCOPES = [
  { key: 'enterprise', label: 'Enterprise' },
  { key: 'course', label: 'Course' },
  { key: 'learner', label: 'Learner' },
];

const refLabel = { enterprise: 'Organization ID', course: 'Course ID', learner: 'User ID' };

/**
 * "Memory files" manager for the chatbot. Stores hybrid .md memory in
 * yaki_memory scoped to enterprise / course / learner. A URL or pasted text can
 * be auto-analyzed into a draft, then edited and saved. These files are pulled
 * into prompts as reusable context.
 */
export default function MemoryPanel({ open, onClose, userId }) {
  const [scope, setScope] = useState('course');
  const [refId, setRefId] = useState('');
  const [files, setFiles] = useState([]);
  const [fileName, setFileName] = useState('');
  const [editor, setEditor] = useState('');
  const [sourceType, setSourceType] = useState('url');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState('');

  const refresh = useCallback(() => {
    if (scope && refId) listMemoryFiles(scope, refId).then(setFiles).catch(() => setFiles([]));
    else setFiles([]);
  }, [scope, refId]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);
  useEffect(() => { if (scope === 'learner' && userId) setRefId(String(userId)); }, [scope, userId]);

  if (!open) return null;

  const onAnalyze = async () => {
    if (!scope || !refId || !source.trim()) return;
    setBusy('analyze');
    try {
      const md = await analyzeMemorySource({
        scope, refId, fileName: fileName || 'memory', sourceType, source,
      });
      setEditor(md);
    } catch (_) {
      setEditor('# Memory\n\n(Could not auto-analyze the source — you can write the memory manually here.)');
    }
    setBusy('');
  };

  const onSave = async () => {
    if (!scope || !refId || !fileName.trim() || !editor.trim()) return;
    setBusy('save');
    await saveMemoryFile({ scope, refId, fileName: fileName.trim(), markdown: editor });
    setBusy('');
    setSource('');
    refresh();
  };

  const onLoad = (f) => { setFileName(f.fileName); setEditor(f.markdown); };
  const onDelete = async (e, f) => {
    e.stopPropagation();
    await deleteMemoryFile(f.scope, f.refId, f.fileName);
    if (f.fileName === fileName) { setFileName(''); setEditor(''); }
    refresh();
  };

  const input = {
    width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #d9d7e6',
    fontSize: 12.5, boxSizing: 'border-box', marginTop: 4,
  };
  const chip = (active) => ({
    padding: '4px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
    border: '1px solid ' + (active ? '#5b54e6' : '#d9d7e6'),
    background: active ? '#5b54e6' : '#fff', color: active ? '#fff' : '#444',
  });
  const btn = (primary) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px',
    borderRadius: 7, fontSize: 12.5, cursor: 'pointer', border: 'none',
    background: primary ? '#5b54e6' : '#eceaf6', color: primary ? '#fff' : '#42407a',
  });

  return (
    <div
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: '#fff', zIndex: 8, overflowY: 'auto', padding: 14,
        borderRadius: 16, display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: 14 }}>Memory files</strong>
        <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
          <X size={18} />
        </button>
      </div>

      {/* scope + ref id */}
      <div style={{ display: 'flex', gap: 6 }}>
        {SCOPES.map((s) => (
          <button key={s.key} style={chip(scope === s.key)} onClick={() => { setScope(s.key); setFiles([]); }}>
            {s.label}
          </button>
        ))}
      </div>
      <label style={{ fontSize: 11.5, color: '#777' }}>
        {refLabel[scope]}
        <input style={input} value={refId} onChange={(e) => setRefId(e.target.value)} placeholder={refLabel[scope]} />
      </label>

      {/* existing files */}
      <div>
        <div style={{ fontSize: 11.5, color: '#777', marginBottom: 4 }}>Saved files</div>
        {files.length === 0 ? (
          <div style={{ fontSize: 12, color: '#9a99a8' }}>No files for this {scope} yet.</div>
        ) : (
          files.map((f) => (
            <div
              key={f.fileName}
              onClick={() => onLoad(f)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 8px', borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                background: f.fileName === fileName ? '#ece9ff' : '#f4f5f7',
              }}
            >
              <span style={{ fontSize: 12 }}>{`${scope}-${f.refId}-${f.fileName}.md`}</span>
              <button onClick={(e) => onDelete(e, f)} aria-label="Delete" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#b4304a' }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>

      {/* create / edit */}
      <label style={{ fontSize: 11.5, color: '#777' }}>
        File name (e.g. location, standard, attachment1, url)
        <input style={input} value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="location" />
      </label>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button style={chip(sourceType === 'url')} onClick={() => setSourceType('url')}>URL</button>
        <button style={chip(sourceType === 'text')} onClick={() => setSourceType('text')}>Text</button>
      </div>
      <textarea
        style={{ ...input, minHeight: sourceType === 'url' ? 34 : 70, resize: 'vertical' }}
        value={source}
        onChange={(e) => setSource(e.target.value)}
        placeholder={sourceType === 'url' ? 'https://… reference link' : 'Paste reference text to analyze…'}
      />
      <button style={btn(false)} disabled={busy === 'analyze'} onClick={onAnalyze}>
        <Sparkles size={14} /> {busy === 'analyze' ? 'Analyzing…' : 'Analyze into memory'}
      </button>

      <label style={{ fontSize: 11.5, color: '#777' }}>
        Memory (.md) — review &amp; edit before saving
        <textarea
          style={{ ...input, minHeight: 150, fontFamily: 'monospace', fontSize: 11.5, resize: 'vertical' }}
          value={editor}
          onChange={(e) => setEditor(e.target.value)}
          placeholder="Analyzed memory will appear here; you can edit it freely."
        />
      </label>
      <button style={btn(true)} disabled={busy === 'save'} onClick={onSave}>
        <Save size={14} /> {busy === 'save' ? 'Saving…' : 'Save memory file'}
      </button>
    </div>
  );
}
